import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  where,
  writeBatch,
  increment,
  updateDoc
} from 'firebase/firestore'
import { db } from '../config/firebase'
import { persistableDesignImage } from './designStorage'
import {
  NEWER_COMMUNITY_POST_FIELDS,
  buildCommunityPost,
  communityPostId,
  isPostForDesign
} from '../utils/communityPost'

export async function publishDesignToCommunity(design, user, { allowRemix = true } = {}) {
  if (!user?.uid) throw new Error('Sign in to share designs')
  if (!design?.id) throw new Error('Save this design before sharing it')
  const source = design.mainImage || design.image
  if (!source) throw new Error('This design has no image to share yet')

  const mainImage = await persistableDesignImage(source, user.uid)
  const post = buildCommunityPost(design, user, mainImage, { allowRemix })
  const postId = communityPostId(user.uid, post.designId)
  const ref = doc(db, 'communityDesigns', postId)
  const existing = await getDoc(ref)
  const previous = existing.exists() ? existing.data() : null

  const data = {
    ...post,
    likeCount: previous?.likeCount || 0,
    createdAt: previous?.createdAt || serverTimestamp(),
    updatedAt: serverTimestamp()
  }
  try {
    await setDoc(ref, data)
  } catch (err) {
    if (err?.code !== 'permission-denied' || data.allowRemix === false) throw err
    const older = { ...data }
    for (const field of NEWER_COMMUNITY_POST_FIELDS) delete older[field]
    await setDoc(ref, older)
  }
  return { ...post, id: postId }
}

export async function fetchCommunityPost(postId) {
  if (!postId) return null
  const snap = await getDoc(doc(db, 'communityDesigns', postId))
  return snap.exists() ? { id: snap.id, ...snap.data() } : null
}

/** Removes every public post the user made from this design. */
export async function unshareDesign(designId, uid) {
  if (!uid || !designId) return 0
  const snap = await getDocs(query(collection(db, 'communityDesigns'), where('userId', '==', uid)))
  const mine = snap.docs.filter((d) => isPostForDesign({ id: d.id, ...d.data() }, uid, designId))
  await Promise.all(mine.map((d) => deleteDoc(d.ref)))
  return mine.length
}

/** Copies the creator's remix setting onto every post they already shared. */
export async function setRemixAllowedOnMyPosts(uid, allowRemix) {
  if (!uid) return { updated: 0, failed: 0 }
  const snap = await getDocs(query(collection(db, 'communityDesigns'), where('userId', '==', uid)))
  const results = await Promise.allSettled(
    snap.docs.map((d) => updateDoc(d.ref, { allowRemix: allowRemix !== false, updatedAt: serverTimestamp() }))
  )
  const failed = results.filter((r) => r.status === 'rejected').length
  return { updated: results.length - failed, failed }
}

export async function fetchMyCommunityPosts(uid) {
  if (!uid) return []
  const snap = await getDocs(query(collection(db, 'communityDesigns'), where('userId', '==', uid)))
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }))
}

export async function deleteCommunityPost(postId) {
  await deleteDoc(doc(db, 'communityDesigns', postId))
}

export async function fetchPublicDesigns({ max = 24 } = {}) {
  // Simple collection read, no composite index required. Sort/filter client-side.
  const snap = await getDocs(collection(db, 'communityDesigns'))
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((d) => d.isPublic !== false)
    .sort((a, b) => toMillis(b.createdAt) - toMillis(a.createdAt))
    .slice(0, max)
}

function toMillis(value) {
  if (!value) return 0
  if (typeof value.toMillis === 'function') return value.toMillis()
  if (typeof value.seconds === 'number') return value.seconds * 1000
  if (value instanceof Date) return value.getTime()
  const parsed = Date.parse(value)
  return Number.isFinite(parsed) ? parsed : 0
}

/** One like per account: the like doc and the +1 commit together or not at all. */
export async function likeCommunityDesign(designId, userId) {
  if (!userId) throw new Error('Sign in to like designs')
  const likeRef = doc(db, 'communityDesigns', designId, 'likes', userId)
  const likeSnap = await getDoc(likeRef)
  if (likeSnap.exists()) return false
  const batch = writeBatch(db)
  batch.set(likeRef, { userId, createdAt: serverTimestamp() })
  batch.update(doc(db, 'communityDesigns', designId), { likeCount: increment(1) })
  await batch.commit()
  return true
}
